// Utility function to calculate current week number
export const getCurrentWeekNumber = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now - start) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + start.getDay() + 1) / 7);
  return weekNumber.toString();
};

// Utility function to calculate week number based on a specific date
export const getWeekNumberFromDate = (dateString) => {
  // Handle both datetime-local string format and Date objects
  let targetDate;
  if (typeof dateString === 'string') {
    // For datetime-local format (YYYY-MM-DDTHH:MM), create date in local timezone
    targetDate = new Date(dateString);
  } else {
    targetDate = new Date(dateString);
  }
  
  // Use local timezone date components
  const year = targetDate.getFullYear();
  const start = new Date(year, 0, 1); // January 1st of the same year
  const days = Math.floor((targetDate - start) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + start.getDay() + 1) / 7);
  return weekNumber.toString();
};

// Utility function to generate PM/SR number based on a specific date
export const generateDocumentNumberForDate = async (type, qdfAPI, creationDate) => {
  try {
    // Get date in YYYYMMDD format from the provided creation date
    // Handle both datetime-local string format and Date objects
    let targetDate;
    if (typeof creationDate === 'string') {
      // For datetime-local format (YYYY-MM-DDTHH:MM), create date in local timezone
      targetDate = new Date(creationDate);
    } else {
      targetDate = new Date(creationDate);
    }
    
    // Extract date components in local timezone to avoid UTC conversion issues
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    
    console.log(`🎯 GENERATING ${type} NUMBER FOR DATE: ${dateStr}`);
    console.log(`📅 Target date: ${targetDate.toString()}`);
    console.log(`📅 Local date components: Year=${year}, Month=${month}, Day=${day}`);
    
    // Get the prefix we're looking for
    const prefix = type === 'PM' ? 'PM' : 'SR';
    console.log(`🏷️ Looking for prefix: ${prefix}`);
    
    // Fetch ALL reports (no date filter) to be absolutely sure we get everything
    console.log('🔍 Fetching ALL reports to find existing numbers...');
    const allReportsResponse = await qdfAPI.getReports({ 
      limit: 10000  // Get everything
    });
    
    console.log('📊 FULL API RESPONSE:', JSON.stringify(allReportsResponse.data, null, 2));
    const allReports = allReportsResponse.data.reports || [];
    console.log(`📋 Found ${allReports.length} total reports`);
    
    // Filter for the target date's reports manually
    const targetDateReports = allReports.filter(report => {
      if (!report.scNumber) return false;
      const reportDateMatch = report.scNumber.match(/^(PM|SR)(\d{8})\d{3}$/);
      if (!reportDateMatch) return false;
      const reportDate = reportDateMatch[2];
      console.log(`📊 Report ${report.scNumber} has date: ${reportDate}, comparing to: ${dateStr}`);
      return reportDate === dateStr;
    });
    
    console.log(`📅 Found ${targetDateReports.length} reports for target date (${dateStr})`);
    targetDateReports.forEach(report => {
      console.log(`   - ${report.scNumber} (${report.maintenanceType})`);
    });
    
    // Find the highest number for the target date with the SAME PREFIX only
    let lastNumber = 0;
    const pattern = new RegExp(`^${prefix}${dateStr}(\\d{3})$`);
    
    console.log(`🔍 Looking for pattern: ${pattern} (${prefix} numbers only for date ${dateStr})`);
    
    targetDateReports.forEach((report, index) => {
      console.log(`📝 Checking report ${index + 1}: SC Number = "${report.scNumber}"`);
      const match = report.scNumber?.match(pattern);
      if (match) {
        const num = parseInt(match[1], 10);
        console.log(`✅ FOUND MATCHING ${prefix} NUMBER: ${num} from SC Number: ${report.scNumber}`);
        if (num > lastNumber) {
          lastNumber = num;
          console.log(`🔢 Updated lastNumber to: ${lastNumber}`);
        }
      } else {
        console.log(`❌ No match for SC Number: ${report.scNumber} (not a ${prefix} number for ${dateStr})`);
      }
    });
    
    console.log(`🎯 FINAL LAST ${prefix} NUMBER FOUND FOR ${dateStr}: ${lastNumber}`);
    
    // Generate next number
    const nextNumber = (lastNumber + 1).toString().padStart(3, '0');
    const newDocNumber = `${prefix}${dateStr}${nextNumber}`;
    
    console.log(`✨ GENERATED NEW ${prefix} DOCUMENT NUMBER: ${newDocNumber}`);
    
    return newDocNumber;
    
  } catch (error) {
    console.error('Error generating document number:', error);
    // Fallback to timestamp-based number if API fails
    const targetDate = new Date(creationDate);
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    const timeStr = targetDate.getTime().toString().slice(-3);
    return `${type === 'PM' ? 'PM' : 'SR'}${dateStr}${timeStr}`;
  }
};

// Utility function to generate PM/SR number (current date - kept for backward compatibility)
export const generateDocumentNumber = async (type, qdfAPI) => {
  const now = new Date();
  return generateDocumentNumberForDate(type, qdfAPI, now);
};

// Format datetime for PDF
export const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '';
  
  // Handle both ISO string and datetime-local formats
  const date = new Date(dateTimeString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  // Manual formatting to ensure consistency
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const timeString = `${hours}:${minutes}`;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;
  
  return `${timeString} ${dateString}`;
};

// Generate PDF filename
export const generatePDFFileName = (formData, maintenanceType) => {
  let fileName;
  if (maintenanceType === 'preventive') {
    fileName = [formData.scNumber, `QDF_${formData.ppmType}_PPM`]
      .filter(Boolean)
      .join('_') + '.pdf';
  } else {
    fileName = [formData.scNumber, 'QDF_CM']
      .filter(Boolean)
      .join('_') + '.pdf';
  }
  return fileName;
};
